/**
 * Which build an error came from.
 *
 * WHY THIS EXISTS
 *
 * Every mobile event reaching Sentry today has an empty release. That is not a
 * cosmetic gap: it removes the one fact that makes a report decidable.
 *
 * The keychain error of 13 September (CAPACITOR-3) is the example. Its ticket
 * ends on "we cannot act until we know whether it comes back on a store
 * build", because a keychain refusal on a development build reinstalled after
 * a provisioning change is an artefact, and the same refusal on an App Store
 * build means onboarding cannot store the session token. Sentry knows which
 * one it was and cannot say, so the ticket waits.
 *
 * The same is true of every regression question: "did 2.1.7 introduce this"
 * has no answer while the field is empty.
 *
 * WHY A beforeSend HOOK RATHER THAN THE init OPTION
 *
 * The version is only readable through Capacitor's App.getInfo(), which is
 * asynchronous. Waiting for it before Sentry.init would leave a window where a
 * crash during startup is not captured at all, which is the worst moment to be
 * blind. So the SDK starts immediately, the version lands a tick later, and
 * events are stamped as they leave.
 *
 * An event sent in that first tick carries no release, which is correct: it is
 * better than a release we do not know to be true.
 */

export interface AppBuild {
  /** Marketing version, e.g. 2.1.7. */
  version?: string;
  /** Build number, e.g. 114 on iOS, versionCode on Android. */
  build?: string;
}

/**
 * The version an application served by a developer's own machine reports.
 * environment.ts, the one the dev server uses, answers this rather than asking
 * Capacitor, which has nothing to tell it outside a real build.
 */
export const LOCAL_BUILD_VERSION = 'local';

/**
 * Whether these errors come from somebody's laptop rather than from a build
 * somebody installed.
 */
export function isLocalBuild(build: AppBuild): boolean {
  return build.version?.trim().toLowerCase() === LOCAL_BUILD_VERSION;
}

/** What Sentry needs of an event, and nothing more, so this can be tested. */
export interface StampableEvent {
  release?: string;
  dist?: string;
}

/**
 * Sentry's convention is name@version. Keeping the package name in front means
 * the mobile releases never collide with the API's own in the same
 * organisation.
 */
export function releaseNameOf(build: AppBuild): string | undefined {
  const version = build.version?.trim();
  return version ? `mobile-ionic@${version}` : undefined;
}

/**
 * Stamps an outgoing event, and never overwrites what is already there.
 *
 * The SDK or a future native layer may set these itself; a hook that insists
 * would replace a precise value with a less precise one.
 */
export function stampBuild<T extends StampableEvent>(event: T, build: AppBuild): T {
  const release = releaseNameOf(build);
  if (release && !event.release) {
    event.release = release;
  }
  const dist = build.build?.trim();
  if (dist && !event.dist) {
    event.dist = dist;
  }
  return event;
}
